"use client"

import { useState, useEffect } from "react"
import axiosInstance from "../../../utils/axiosInstance"
import { API_ENDPOINTS } from "../../../constants/config"

const FeePayment = ({ fees, onPaymentComplete, onBack, studentId }) => {
  const [paymentMethod, setPaymentMethod] = useState("gateway")
  const [transactionId, setTransactionId] = useState("")
  const [remarks, setRemarks] = useState("")
  const [isLoading, setIsLoading] = useState(false)
  const [fileProof, setFileProof] = useState(null)
  const [paymentSuccess, setPaymentSuccess] = useState(false)
  const [qrData, setQrData] = useState("")
  const [scholarship, setScholarship] = useState(null)
  const [lateFeeDetails, setLateFeeDetails] = useState(null)
  const [razorpayOrder, setRazorpayOrder] = useState(null)
  const [paymentMethods, setPaymentMethods] = useState([])
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState(null)
  const [loadingPaymentMethods, setLoadingPaymentMethods] = useState(true)

  // Fetch payment methods configured by admin
  useEffect(() => {
    const fetchPaymentMethods = async () => {
      try {
        const token = localStorage.getItem("token")
        const response = await axiosInstance.get(API_ENDPOINTS.paymentMethodsActive, {
          headers: { Authorization: `Bearer ${token}` },
        })
        
        const methods = response.data.data || []
        console.log("Payment Methods:", methods)
        const activeMethods = methods.filter(m => m.isActive)
        setPaymentMethods(activeMethods)
        
        // Gateway (Razorpay) is always the default
        setPaymentMethod("gateway")
      } catch (error) {
        console.error("Error fetching payment methods:", error)
      } finally {
        setLoadingPaymentMethods(false)
      }
    }
    
    fetchPaymentMethods()
  }, [])

  useEffect(() => {
    const fetchScholarship = async () => {
      try {
        if (!studentId) {
          return
        }

        const token = localStorage.getItem("token")
        const user = JSON.parse(localStorage.getItem("user") || "{}")
        const schoolId = user.school
        
        const response = await axiosInstance.get(API_ENDPOINTS.scholarshipsStudent(studentId, schoolId), {
          headers: { Authorization: `Bearer ${token}` },
        })
        if (response.data && response.data.length > 0) {
          setScholarship(response.data[0])
        }
      } catch (error) {
        console.error("Error fetching scholarship:", error)
      }
    }

    if (studentId) {
      fetchScholarship()
    }
  }, [studentId])

  useEffect(() => {
    if (fees && selectedPaymentMethod?.upiId) {
      const totalAmount = calculateTotalAmount()
      // Generate QR code data with UPI details from selected payment method
      const qrCodeData = `upi://pay?pa=${selectedPaymentMethod.upiId}&pn=${encodeURIComponent("School Payment")}&am=${totalAmount.baseTotal}&cu=INR&tn=${encodeURIComponent(`Fee payment for ${fees.quarter}`)}`
      setQrData(qrCodeData)
    }
  }, [fees, selectedPaymentMethod, lateFeeDetails])

  const fetchLateFee = async (feeId) => {
    try {
      const token = localStorage.getItem("token")
      const response = await axiosInstance.get(API_ENDPOINTS.feesLateFee(feeId), {
        headers: { Authorization: `Bearer ${token}` },
      })
      return response.data.data
    } catch (error) {
      console.error(`[Payment] Error fetching late fee for ${feeId}:`, error)
      console.error("[Payment] Late fee API error details:", error.response?.data)
      return null
    }
  }

  useEffect(() => {
    const fetchLateFeeData = async () => {
      if (fees?._id) {
        const lateFeeData = await fetchLateFee(fees._id)
        setLateFeeDetails(lateFeeData)
      } else {
        // If no _id, try to find it from student fees
        if (studentId && fees?.quarter) {
          try {
            const token = localStorage.getItem("token")
            const response = await axiosInstance.get(API_ENDPOINTS.feesStudentFees(studentId), {
              headers: { Authorization: `Bearer ${token}` },
            })
            const studentFees = response.data.data.fees || []
            const matchingFee = studentFees.find((fee) => fee.quarter?.toLowerCase() === fees.quarter?.toLowerCase())
            if (matchingFee?._id) {
              const lateFeeData = await fetchLateFee(matchingFee._id)
              setLateFeeDetails(lateFeeData)
            }
          } catch (error) {
            console.error("[Payment] Error finding fee by quarter:", error)
          }
        }
      }
    }

    fetchLateFeeData()
  }, [fees, studentId])

  const calculateTotalAmount = () => {
    const baseAmount = Array.isArray(fees?.feeBreakup)
      ? fees.feeBreakup.reduce((sum, item) => sum + Math.abs(item.amount || 0), 0)
      : 0;
    const lateFeeAmount = lateFeeDetails?.lateFeeAmount ?? 0;
    const baseTotal = baseAmount + lateFeeAmount;
    const platformFee = fees?.platformFee ?? 0;
    const total = fees?.totalPayable ?? (baseTotal + platformFee);
    return {
      baseAmount,
      lateFeeAmount,
      baseTotal,
      platformFee,
      total,
    };
  };


  const handleFileChange = (e) => {
    setFileProof(e.target.files[0])
  }

  // Create Razorpay order
  const createRazorpayOrder = async () => {
    try {
      const token = localStorage.getItem("token")
      const feeCalculation = calculateTotalAmount()
      const baseAmount = feeCalculation.baseTotal // Send base amount without platform fee

      // Validate required data
      if (!fees?.quarter) {
        throw new Error("Quarter information is missing")
      }
      if (!studentId) {
        throw new Error("Student ID is missing")
      }
      if (!baseAmount || baseAmount <= 0) {
        throw new Error("Invalid payment amount")
      }

      console.log('📤 Sending data to backend:', {
        quarter: fees.quarter,
        amount: baseAmount, // Send base amount, backend will add platform fee
        studentId: studentId
      })

      const response = await axiosInstance.post(
        API_ENDPOINTS.feesRazorpayCreateOrder,
        {
          quarter: fees.quarter,
          amount: baseAmount, // Send base amount without platform fee
          studentId: studentId,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      )

      if (response.data.status === "success") {
        setRazorpayOrder(response.data.data)
        return response.data.data
      } else {
        throw new Error(response.data.message || "Failed to create order")
      }
    } catch (error) {
      console.error("Error creating Razorpay order:", error)
      throw error
    }
  }

  // Initialize Razorpay payment
  const initializeRazorpayPayment = async () => {
    try {
      const orderData = await createRazorpayOrder()
      
      const options = {
        key: orderData.config.key,
        amount: orderData.order.amount,
        currency: orderData.currency,
        name: orderData.config.name,
        description: orderData.config.description,
        image: orderData.config.image,
        order_id: orderData.order.id,
        handler: async function (response) {
          await handleRazorpaySuccess(response)
        },
        prefill: {
          name: "Student Name", // You can get this from student data
          email: "student@school.com", // You can get this from student data
          contact: "9999999999" // You can get this from student data
        },
        notes: {
          address: "School Address"
        },
        theme: orderData.config.theme,
        modal: {
          ondismiss: function() {
            console.log("Payment modal closed")
          }
        }
      }

      const rzp = new window.Razorpay(options)
      rzp.open()
    } catch (error) {
      console.error("Error initializing Razorpay payment:", error)
      alert("Payment Failed: " + (error.response?.data?.message || "An error occurred while initializing payment"))
    }
  }

  // Handle Razorpay payment success
  const handleRazorpaySuccess = async (response) => {
    try {
      setIsLoading(true)
      const token = localStorage.getItem("token")

      const verifyResponse = await axiosInstance.post(
        API_ENDPOINTS.feesRazorpayVerifyPayment,
        {
          razorpay_order_id: response.razorpay_order_id,
          razorpay_payment_id: response.razorpay_payment_id,
          razorpay_signature: response.razorpay_signature,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      )

      if (verifyResponse.data.status === "success") {
        setPaymentSuccess(true)
        alert("Payment Successful! Your fee payment was processed successfully")
        
        setTimeout(() => {
          if (onPaymentComplete) {
            onPaymentComplete()
          }
        }, 2000)
      } else {
        throw new Error(verifyResponse.data.message || "Payment verification failed")
      }
    } catch (error) {
      console.error("Error verifying payment:", error)
      alert("Payment Verification Failed: " + (error.response?.data?.message || "An error occurred while verifying your payment"))
    } finally {
      setIsLoading(false)
    }
  }

  const handleGatewayPayment = async () => {
    if (!fees) {
      alert("Fee information is missing")
      return
    }

    setIsLoading(true)
    try {
      await initializeRazorpayPayment()
    } catch (error) {
      console.error("Error processing Razorpay payment:", error)
      alert("Payment Failed: " + (error.response?.data?.message || "An error occurred while processing your payment"))
    } finally {
      setIsLoading(false)
    }
  }

  const handleUpiPayment = async () => {
    if (!fees || !transactionId) {
      alert("Please provide transaction ID")
      return
    }

    setIsLoading(true)
    try {
      const token = localStorage.getItem("token")
      const totalAmount = calculateTotalAmount()
      
      // Determine payment method type
      let paymentMethodType = "QR"
      if (paymentMethod === "bank") {
        paymentMethodType = "Bank Transfer"
      } else if (paymentMethod === "upi") {
        paymentMethodType = selectedPaymentMethod?.qrCodeImage ? "QR" : "UPI"
      }

      if (fileProof) {
        const formData = new FormData()
        if (studentId) formData.append("studentId", studentId)
        formData.append("quarter", fees.quarter)
        formData.append("amount", String(totalAmount.baseTotal))
        formData.append("paymentMethod", paymentMethodType)
        formData.append("transactionId", transactionId)
        formData.append("remarks", remarks || "")
        formData.append("paymentProof", fileProof)
        await axiosInstance.post(API_ENDPOINTS.feesPayQuarterly, formData, {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "multipart/form-data",
          },
        })
      } else {
        await axiosInstance.post(
          API_ENDPOINTS.feesPayQuarterly,
          {
            studentId: studentId,
            quarter: fees.quarter,
            amount: totalAmount.baseTotal,
            paymentMethod: paymentMethodType,
            transactionId,
            remarks,
          },
          { headers: { Authorization: `Bearer ${token}` } },
        )
      }

      setPaymentSuccess(true)
      const methodName = paymentMethod === "bank" ? "Bank Transfer" : "UPI/QR"
      alert(`Payment Submitted! Your ${methodName} payment details have been submitted for verification`)

      setTimeout(() => {
        if (onPaymentComplete) {
          onPaymentComplete()
        }
      }, 2000)
    } catch (error) {
      console.error("Error submitting payment:", error)
      alert(
        "Submission Failed: " +
          (error.response?.data?.message || "An error occurred while submitting your payment details"),
      )
    } finally {
      setIsLoading(false)
    }
  }

  // Load Razorpay script
  useEffect(() => {
    const loadRazorpayScript = () => {
      return new Promise((resolve) => {
        if (window.Razorpay) {
          resolve()
          return
        }

        const script = document.createElement('script')
        script.src = 'https://checkout.razorpay.com/v1/checkout.js'
        script.onload = () => resolve()
        script.onerror = () => {
          console.error('Failed to load Razorpay script')
          resolve()
        }
        document.body.appendChild(script)
      })
    }

    loadRazorpayScript()
  }, [])

  if (paymentSuccess) {
    return (
      <div className="w-full max-w-3xl mx-auto bg-white rounded-2xl shadow-lg overflow-hidden">
        <div className="flex flex-col items-center justify-center py-12">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-4">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-8 w-8 text-green-600"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <h2 className="text-2xl font-bold text-center mb-2">Payment Successful!</h2>
          <p className="text-gray-500 text-center mb-6">
            Your payment has been {paymentMethod === "gateway" ? "processed successfully" : "submitted for verification"}.
          </p>
          <p className="text-sm text-gray-400 text-center">Redirecting to fee page...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="w-full bg-white rounded-2xl shadow-xl overflow-hidden">
      <div className="bg-gradient-to-r from-blue-600 to-blue-700 p-6">
        <h2 className="text-2xl font-bold text-white">Fee Payment</h2>
        <p className="text-sm text-blue-100 mt-1">Secure payment gateway for {fees?.quarter?.toUpperCase()} fees</p>
      </div>

      <div className="p-6">
        {/* Fee Info Summary */}
        <div className="mb-6 flex items-center justify-between bg-gradient-to-r from-gray-50 to-gray-100 p-4 rounded-lg border-l-4 border-blue-500">
          <div>
            <h3 className="text-xl font-bold text-gray-800">₹{calculateTotalAmount().baseTotal.toLocaleString()}</h3>
            <p className="text-sm text-gray-600">
              {fees?.dueDate && `Due: ${new Date(fees.dueDate).toLocaleDateString()}`}
              {lateFeeDetails && lateFeeDetails.lateFeeAmount > 0 && (
                <span className="ml-2 text-red-600 font-medium">+₹{lateFeeDetails.lateFeeAmount.toLocaleString()} late fee</span>
              )}
            </p>
          </div>
          <div className="text-right">
            <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
              fees?.status === 'success' ? 'bg-green-100 text-green-800' :
              fees?.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
              'bg-gray-100 text-gray-800'
            }`}>
              {fees?.status?.charAt(0).toUpperCase() + fees?.status?.slice(1)}
            </span>
          </div>
        </div>

        {loadingPaymentMethods ? (
          <div className="mb-6 text-center text-gray-500">
            Loading payment methods...
          </div>
        ) : (
          <div className="mb-6">
            <div className="grid grid-cols-3 gap-3">
              {/* Razorpay Gateway Card */}
              <button
                className={`p-4 rounded-xl border-2 transition-all ${
                  paymentMethod === "gateway"
                    ? "border-blue-500 bg-blue-50 shadow-md"
                    : "border-gray-200 bg-white hover:border-blue-300"
                }`}
                onClick={() => {
                  setPaymentMethod("gateway")
                  setSelectedPaymentMethod(null)
                }}
              >
                <div className="flex flex-col items-center text-center">
                  <div className={`w-12 h-12 rounded-full flex items-center justify-center mb-2 ${
                    paymentMethod === "gateway" ? "bg-blue-600" : "bg-gray-200"
                  }`}>
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className={`h-6 w-6 ${paymentMethod === "gateway" ? "text-white" : "text-gray-600"}`}
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                    </svg>
                  </div>
                  <span className={`font-semibold text-sm ${paymentMethod === "gateway" ? "text-blue-700" : "text-gray-700"}`}>Razorpay</span>
                  <span className="text-xs text-gray-500 mt-1">Instant</span>
                </div>
              </button>

              {/* UPI Payment Card */}
              <button
                className={`p-4 rounded-xl border-2 transition-all ${
                  paymentMethod === "upi"
                    ? "border-blue-500 bg-blue-50 shadow-md"
                    : "border-gray-200 bg-white hover:border-blue-300"
                }`}
                onClick={() => {
                  setPaymentMethod("upi")
                  const upiMethods = paymentMethods.filter(m => m.type === 'upi')
                  if (upiMethods.length > 0) {
                    setSelectedPaymentMethod(upiMethods[0])
                  } else {
                    setSelectedPaymentMethod(null)
                  }
                }}
              >
                <div className="flex flex-col items-center text-center">
                  <div className={`w-12 h-12 rounded-full flex items-center justify-center mb-2 ${
                    paymentMethod === "upi" ? "bg-blue-600" : "bg-gray-200"
                  }`}>
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className={`h-6 w-6 ${paymentMethod === "upi" ? "text-white" : "text-gray-600"}`}
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v1m6 11h2m-6 0h-2v4m0-11v3m0 0h.01M12 12h4.01M16 20h4M4 12h4m12 0h.01M5 8h2a1 1 0 001-1V5a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1zm12 0h2a1 1 0 001-1V5a1 1 0 00-1-1h-2a1 1 0 00-1 1v2a1 1 0 001 1zM5 20h2a1 1 0 001-1v-2a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1z" />
                    </svg>
                  </div>
                  <span className={`font-semibold text-sm ${paymentMethod === "upi" ? "text-blue-700" : "text-gray-700"}`}>UPI / QR</span>
                  <span className="text-xs text-gray-500 mt-1">Verify later</span>
                </div>
              </button>

              {/* Bank Transfer Card */}
              <button
                className={`p-4 rounded-xl border-2 transition-all ${
                  paymentMethod === "bank"
                    ? "border-blue-500 bg-blue-50 shadow-md"
                    : "border-gray-200 bg-white hover:border-blue-300"
                }`}
                onClick={() => {
                  setPaymentMethod("bank")
                  const bankMethods = paymentMethods.filter(m => m.type === 'bank')
                  if (bankMethods.length > 0) {
                    setSelectedPaymentMethod(bankMethods[0])
                  } else {
                    setSelectedPaymentMethod(null)
                  }
                }}
              >
                <div className="flex flex-col items-center text-center">
                  <div className={`w-12 h-12 rounded-full flex items-center justify-center mb-2 ${
                    paymentMethod === "bank" ? "bg-blue-600" : "bg-gray-200"
                  }`}>
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className={`h-6 w-6 ${paymentMethod === "bank" ? "text-white" : "text-gray-600"}`}
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 14v3m4-3v3m4-3v3M3 21h18M3 10h18M3 7l9-4 9 4M4 10h16v11H4V10z" />
                    </svg>
                  </div>
                  <span className={`font-semibold text-sm ${paymentMethod === "bank" ? "text-blue-700" : "text-gray-700"}`}>Bank Transfer</span>
                  <span className="text-xs text-gray-500 mt-1">Verify later</span>
                </div>
              </button>
            </div>
          </div>
        )}

        {/* Payment Method Selection Section */}
        <div className="mb-6">
          {/* UPI Method Selection Dropdown */}
          {paymentMethod === "upi" && (
            <div className="space-y-2">
              <label htmlFor="upi-method-select" className="block text-sm font-medium text-gray-700">
                Select UPI Payment Method
              </label>
              {paymentMethods.filter(m => m.type === 'upi').length > 0 ? (
                <select
                  id="upi-method-select"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  onChange={(e) => {
                    const method = paymentMethods.find(m => m._id === e.target.value)
                    setSelectedPaymentMethod(method)
                  }}
                  value={selectedPaymentMethod?._id || ""}
                >
                  <option value="">Select a UPI method</option>
                  {paymentMethods.filter(m => m.type === 'upi').map((method) => (
                    <option key={method._id} value={method._id}>
                      {method.qrCodeImage ? `QR Code - ${method.upiId}` : `UPI ID: ${method.upiId}`}
                    </option>
                  ))}
                </select>
              ) : (
                <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-md">
                  <p className="text-sm text-yellow-800">No UPI payment methods configured. Please contact administration.</p>
                </div>
              )}
            </div>
          )}

          {/* Bank Method Selection Dropdown */}
          {paymentMethod === "bank" && (
            <div className="space-y-2">
              <label htmlFor="bank-method-select" className="block text-sm font-medium text-gray-700">
                Select Bank Account
              </label>
              {paymentMethods.filter(m => m.type === 'bank').length > 0 ? (
                <select
                  id="bank-method-select"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  onChange={(e) => {
                    const method = paymentMethods.find(m => m._id === e.target.value)
                    setSelectedPaymentMethod(method)
                  }}
                  value={selectedPaymentMethod?._id || ""}
                >
                  <option value="">Select a bank account</option>
                  {paymentMethods.filter(m => m.type === 'bank').map((method) => (
                    <option key={method._id} value={method._id}>
                      {method.bankName} - {method.accountNumber.slice(-4)}
                    </option>
                  ))}
                </select>
              ) : (
                <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-md">
                  <p className="text-sm text-yellow-800">No bank accounts configured. Please contact administration.</p>
                </div>
              )}
            </div>
          )}
        </div>

        {paymentMethod === "gateway" && (
          <div className="bg-gray-50 p-4 rounded-md mb-6">
            <div className="flex items-center mb-4">
              <img 
                src="https://razorpay.com/favicon.png" 
                alt="Razorpay" 
                className="w-6 h-6 mr-2"
              />
              <p className="text-sm text-gray-600">
                Secure payment powered by Razorpay. You will be redirected to Razorpay's secure payment gateway.
              </p>
            </div>
            
            {/* Fee Breakdown */}
            {fees && (
              <div className="bg-white p-4 rounded-md border mb-4">
                <h4 className="font-semibold text-gray-800 mb-3 flex items-center">
                  <svg className="w-5 h-5 mr-2 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                  </svg>
                  Payment Details
                </h4>
                <div className="space-y-2 text-sm">
                  {/* Fee Breakup Items */}
                  {fees?.feeBreakup?.map((fee, index) => (
                    <div key={index} className="flex justify-between">
                      <span className="text-gray-600">{fee.feeName}</span>
                      <span className="text-gray-800">₹{Math.abs(fee.amount || 0).toLocaleString()}</span>
                    </div>
                  ))}
                  
                  {/* Late Fee */}
                  {lateFeeDetails && lateFeeDetails.lateFeeAmount > 0 && (
                    <div className="flex justify-between">
                      <span className="text-gray-600">Late Fee</span>
                      <span className="text-red-600">+₹{lateFeeDetails.lateFeeAmount.toLocaleString()}</span>
                    </div>
                  )}
                  
                  {/* Scholarship */}
                  {fees?.scholarshipFees > 0 && (
                    <div className="flex justify-between">
                      <span className="text-gray-600">Scholarship Discount</span>
                      <span className="text-green-600">-₹{Math.abs(fees.scholarshipFees).toLocaleString()}</span>
                    </div>
                  )}
                  
                  <div className="border-t pt-2 mt-2">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Subtotal:</span>
                      <span className="font-medium">₹{calculateTotalAmount().baseTotal.toLocaleString()}</span>
                    </div>
                    <div className="flex justify-between mt-1">
                      <span className="text-gray-600 text-xs">Platform Fee (2% + GST):</span>
                      <span className="text-orange-600 text-xs">+₹{calculateTotalAmount().platformFee.toLocaleString()}</span>
                    </div>
                  </div>
                  
                  <div className="border-t pt-2 mt-2 bg-blue-50 -mx-4 px-4 py-2 rounded">
                    <div className="flex justify-between font-bold">
                      <span className="text-gray-800">Total to Pay:</span>
                      <span className="text-blue-600 text-lg">₹{calculateTotalAmount().total.toLocaleString()}</span>
                    </div>
                  </div>
                </div>
              </div>
            )}
            
            <button
              onClick={handleGatewayPayment}
              className="w-full px-4 py-2 bg-blue-600 text-white font-medium rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 transition-colors"
              disabled={!fees || isLoading}
            >
              {isLoading ? "Processing..." : `Pay ₹${calculateTotalAmount().total.toLocaleString()}`}
            </button>
          </div>
        )}

        {paymentMethod === "upi" && (
          <div className="space-y-4 mb-6">
            {selectedPaymentMethod ? (
              <>
                {/* Fee Breakdown for UPI - Moved to top */}
                <div className="bg-white p-4 rounded-md border">
                  <h4 className="font-semibold text-gray-800 mb-3 text-sm">Payment Amount Details</h4>
                  <div className="space-y-2 text-sm">
                    {fees?.feeBreakup?.map((fee, index) => (
                      <div key={index} className="flex justify-between">
                        <span className="text-gray-600">{fee.feeName}</span>
                        <span className="text-gray-800">₹{Math.abs(fee.amount || 0).toLocaleString()}</span>
                      </div>
                    ))}
                    {lateFeeDetails && lateFeeDetails.lateFeeAmount > 0 && (
                      <div className="flex justify-between">
                        <span className="text-gray-600">Late Fee</span>
                        <span className="text-red-600">+₹{lateFeeDetails.lateFeeAmount.toLocaleString()}</span>
                      </div>
                    )}
                    {fees?.scholarshipFees > 0 && (
                      <div className="flex justify-between">
                        <span className="text-gray-600">Scholarship</span>
                        <span className="text-green-600">-₹{Math.abs(fees.scholarshipFees).toLocaleString()}</span>
                      </div>
                    )}
                    <div className="border-t pt-2 mt-2 bg-blue-50 -mx-4 px-4 py-2">
                      <div className="flex justify-between font-bold">
                        <span className="text-gray-800">Total Amount:</span>
                        <span className="text-blue-600">₹{calculateTotalAmount().baseTotal.toLocaleString()}</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Display QR Code */}
                <div className="bg-gray-50 p-4 rounded-md">
                  <h4 className="font-semibold text-gray-800 mb-3">UPI Payment Details</h4>
              
              {selectedPaymentMethod.qrCodeImage ? (
                <div className="flex flex-col items-center justify-center">
                  <div className="bg-white p-4 rounded-md border mb-4">
                    <img
                      src={selectedPaymentMethod.qrCodeImage}
                      alt="Payment QR Code"
                      className="w-48 h-48 mx-auto object-contain"
                    />
                  </div>
                  <p className="text-sm text-center text-gray-600 mb-4">
                    Scan the QR code with any UPI app to make the payment of ₹{calculateTotalAmount().baseTotal.toLocaleString()}
                  </p>
                </div>
              ) : selectedPaymentMethod.upiId && qrData ? (
                <div className="flex flex-col items-center justify-center">
                  <div className="bg-white p-4 rounded-md border mb-4">
                    <img
                      src={`https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(qrData)}`}
                      alt="Payment QR Code"
                      className="w-40 h-40 mx-auto"
                    />
                  </div>
                  <div className="text-center mb-4">
                    <p className="text-sm text-gray-600 mb-2">
                      Scan the QR code or pay to UPI ID:
                    </p>
                    <p className="font-medium text-gray-800 text-lg">{selectedPaymentMethod.upiId}</p>
                    <p className="text-sm text-gray-500 mt-2">Amount: ₹{calculateTotalAmount().baseTotal.toLocaleString()}</p>
                  </div>
                </div>
              ) : (
                <div className="bg-gray-100 p-4 rounded-md text-center">
                  <p className="text-gray-500">UPI ID: {selectedPaymentMethod.upiId}</p>
                  <p className="text-sm text-gray-600 mt-2">Please pay to this UPI ID and enter transaction details below</p>
                </div>
              )}
            </div>

            <div className="space-y-2">
              <label htmlFor="transaction-id" className="block text-sm font-medium text-gray-700">
                Transaction ID *
              </label>
              <input
                id="transaction-id"
                value={transactionId}
                onChange={(e) => setTransactionId(e.target.value)}
                placeholder="Enter UPI transaction ID"
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            <div className="space-y-2">
              <label htmlFor="file-proof" className="block text-sm font-medium text-gray-700">
                Attach file/proof
              </label>
              <input
                id="file-proof"
                type="file"
                onChange={handleFileChange}
                accept="image/*,.pdf"
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            <div className="space-y-2">
              <label htmlFor="remarks" className="block text-sm font-medium text-gray-700">
                Remarks/Comments (not mandatory)
              </label>
              <input
                id="remarks"
                value={remarks}
                onChange={(e) => setRemarks(e.target.value)}
                placeholder="Any additional information"
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            <button
              onClick={handleUpiPayment}
              className="w-full px-4 py-2 bg-blue-600 text-white font-medium rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 transition-colors"
              disabled={!fees || !transactionId || isLoading}
            >
              {isLoading ? "Submitting..." : "Submit Payment Details"}
            </button>
              </>
            ) : (
              <div className="p-4 bg-gray-50 rounded-md text-center">
                <p className="text-gray-600">Please select a UPI payment method from the dropdown above to proceed.</p>
              </div>
            )}
          </div>
        )}

        {paymentMethod === "bank" && (
          <div className="space-y-4 mb-6">
            {selectedPaymentMethod ? (
              <>
                {/* Fee Breakdown for Bank Transfer - Moved to top */}
                <div className="bg-white p-4 rounded-md border">
                  <h4 className="font-semibold text-gray-800 mb-3 text-sm">Payment Amount Details</h4>
                  <div className="space-y-2 text-sm">
                    {fees?.feeBreakup?.map((fee, index) => (
                      <div key={index} className="flex justify-between">
                        <span className="text-gray-600">{fee.feeName}</span>
                        <span className="text-gray-800">₹{Math.abs(fee.amount || 0).toLocaleString()}</span>
                      </div>
                    ))}
                    {lateFeeDetails && lateFeeDetails.lateFeeAmount > 0 && (
                      <div className="flex justify-between">
                        <span className="text-gray-600">Late Fee</span>
                        <span className="text-red-600">+₹{lateFeeDetails.lateFeeAmount.toLocaleString()}</span>
                      </div>
                    )}
                    {fees?.scholarshipFees > 0 && (
                      <div className="flex justify-between">
                        <span className="text-gray-600">Scholarship</span>
                        <span className="text-green-600">-₹{Math.abs(fees.scholarshipFees).toLocaleString()}</span>
                      </div>
                    )}
                    <div className="border-t pt-2 mt-2 bg-blue-50 -mx-4 px-4 py-2">
                      <div className="flex justify-between font-bold">
                        <span className="text-gray-800">Total Amount:</span>
                        <span className="text-blue-600">₹{calculateTotalAmount().baseTotal.toLocaleString()}</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Display Bank Transfer Details */}
                <div className="bg-gray-50 p-4 rounded-md">
                  <h4 className="font-semibold text-gray-800 mb-3">Bank Transfer Details</h4>
              
              <div className="bg-white p-4 rounded-md border space-y-3">
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <p className="text-xs text-gray-500">Bank Name</p>
                    <p className="font-medium text-gray-800">{selectedPaymentMethod.bankName}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">Account Holder</p>
                    <p className="font-medium text-gray-800">{selectedPaymentMethod.accountHolderName || 'N/A'}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">Account Number</p>
                    <p className="font-medium text-gray-800">{selectedPaymentMethod.accountNumber}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">IFSC Code</p>
                    <p className="font-medium text-gray-800">{selectedPaymentMethod.ifscCode}</p>
                  </div>
                  {selectedPaymentMethod.branchName && (
                    <div className="col-span-2">
                      <p className="text-xs text-gray-500">Branch</p>
                      <p className="font-medium text-gray-800">{selectedPaymentMethod.branchName}</p>
                    </div>
                  )}
                </div>
              </div>

              <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-md">
                <p className="text-sm text-blue-800">
                  <strong>Note:</strong> After making the bank transfer, please fill in the transaction details below and submit for verification.
                </p>
              </div>
            </div>

            {/* Transaction Details Form */}
            <div className="space-y-2">
              <label htmlFor="bank-transaction-id" className="block text-sm font-medium text-gray-700">
                Transaction ID / Reference Number *
              </label>
              <input
                id="bank-transaction-id"
                value={transactionId}
                onChange={(e) => setTransactionId(e.target.value)}
                placeholder="Enter bank transaction reference number"
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            <div className="space-y-2">
              <label htmlFor="bank-file-proof" className="block text-sm font-medium text-gray-700">
                Attach Payment Receipt/Proof
              </label>
              <input
                id="bank-file-proof"
                type="file"
                onChange={handleFileChange}
                accept="image/*,.pdf"
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            <div className="space-y-2">
              <label htmlFor="bank-remarks" className="block text-sm font-medium text-gray-700">
                Remarks/Comments (Optional)
              </label>
              <textarea
                id="bank-remarks"
                value={remarks}
                onChange={(e) => setRemarks(e.target.value)}
                placeholder="Any additional information"
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            <button
              onClick={handleUpiPayment}
              className="w-full px-4 py-2 bg-blue-600 text-white font-medium rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 transition-colors"
              disabled={!fees || !transactionId || isLoading}
            >
              {isLoading ? "Submitting..." : "Submit Payment Details"}
            </button>
              </>
            ) : (
              <div className="p-4 bg-gray-50 rounded-md text-center">
                <p className="text-gray-600">Please select a bank account from the dropdown above to proceed.</p>
              </div>
            )}
          </div>
        )}
      </div>

      <div className="bg-gray-50 p-4 border-t flex justify-between">
        <button
          onClick={onBack}
          className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
        >
          Back
        </button>
      </div>
    </div>
  )
}

export default FeePayment